import {
  EMAIL_CLICKED_LINK_CLASS,
  EMAIL_PREVIEW_BODY_CLASS,
  ICON_HORIZONTAL_OFFSET,
  ICON_STYLE_ID,
  ICON_TARGET_ATTR,
} from './emailPreviewConstants';

export const getIframeDocument = (iframe: HTMLIFrameElement | null): Document | null =>
  iframe?.contentDocument || iframe?.contentWindow?.document || null;

const clearExistingIconTargets = (iframeDoc: Document) => {
  iframeDoc
    .querySelectorAll<HTMLElement>(`[${ICON_TARGET_ATTR}="true"]`)
    .forEach((node) => node.removeAttribute(ICON_TARGET_ATTR));
};

export const clearDynamicIconStyles = (iframeDoc: Document) => {
  clearExistingIconTargets(iframeDoc);
  const styleEl = iframeDoc.getElementById(ICON_STYLE_ID) as HTMLStyleElement | null;
  if (styleEl) {
    styleEl.textContent = '';
  }
};

const markIconContainer = (iframeDoc: Document, container: HTMLElement) => {
  clearExistingIconTargets(iframeDoc);
  container.setAttribute(ICON_TARGET_ATTR, 'true');
};

export const applyDynamicIconStyles = (
  iframeDoc: Document,
  container: HTMLElement,
  left: number,
  top: number,
) => {
  markIconContainer(iframeDoc, container);
  const css = `[${ICON_TARGET_ATTR}="true"]::before { left: ${left}px; top: ${top}px; opacity: 0.8; }`;
  let styleEl = iframeDoc.getElementById(ICON_STYLE_ID) as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = iframeDoc.createElement('style');
    styleEl.id = ICON_STYLE_ID;
    (iframeDoc.head || iframeDoc.body || iframeDoc.documentElement)?.appendChild(styleEl);
  }
  styleEl.textContent = css;
};

export const findPositionedContainer = (
  iframeDoc: Document,
  linkElement: HTMLElement,
): HTMLElement | null => {
  const previewBody = iframeDoc.querySelector(`.${EMAIL_PREVIEW_BODY_CLASS}`) as HTMLElement | null;
  let current = linkElement.parentElement as HTMLElement | null;

  while (current) {
    if (previewBody && current === previewBody) {
      return previewBody;
    }

    const position = iframeDoc.defaultView?.getComputedStyle(current).position || 'static';
    if (position !== 'static') {
      return current;
    }

    current = current.parentElement as HTMLElement | null;
  }

  return previewBody;
};

export const disableAllAnchors = (iframeDoc: Document) => {
  const anchors = iframeDoc.querySelectorAll<HTMLAnchorElement>('a[href]');
  anchors.forEach((anchor) => {
    const newAnchor = anchor.cloneNode(true) as HTMLAnchorElement;
    anchor.parentNode?.replaceChild(newAnchor, anchor);

    newAnchor.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    });
  });
};

export const measureIconHeight = (iframeDoc: Document): number => {
  const tempElement = iframeDoc.createElement('span');
  tempElement.style.cssText = `
    position: absolute;
    left: -9999px;
    top: -9999px;
    content: "\\e913";
    font-family: 'Material Icons Outlined';
    font-size: 20pt;
    font-weight: 100;
    color: #ffffff;
    -webkit-text-stroke: 1px #143c34;
    text-shadow: none;
    white-space: nowrap;
    visibility: hidden;
    pointer-events: none;
  `;
  tempElement.textContent = '\ue913';
  iframeDoc.body.appendChild(tempElement);

  void tempElement.offsetHeight;

  const height = tempElement.getBoundingClientRect().height;
  iframeDoc.body.removeChild(tempElement);

  return height || 20;
};

export function positionClickedLinkIcon(
  iframeDoc: Document,
  highlightedLinkId: string | null | undefined,
): boolean {
  disableAllAnchors(iframeDoc);

  if (!highlightedLinkId) {
    return true;
  }

  const body = iframeDoc.querySelector(`.${EMAIL_PREVIEW_BODY_CLASS}`) as HTMLElement | null;
  if (!body) {
    clearDynamicIconStyles(iframeDoc);
    return false;
  }

  const link = iframeDoc.querySelector(`.${EMAIL_CLICKED_LINK_CLASS}`) as HTMLElement | null;
  if (!link) {
    clearDynamicIconStyles(iframeDoc);
    return false;
  }

  const container = findPositionedContainer(iframeDoc, link) || body;
  const linkRect = link.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const iconHeight = measureIconHeight(iframeDoc);

  const leftPosition = linkRect.left - containerRect.left + ICON_HORIZONTAL_OFFSET;
  const topPosition = linkRect.top - containerRect.top + linkRect.height / 2 + iconHeight / 2 - 2;

  applyDynamicIconStyles(iframeDoc, container, leftPosition, topPosition);
  return true;
}

export function waitForIframeAndPositionIcon(
  iframe: HTMLIFrameElement | null,
  highlightedLinkId: string | null | undefined,
  maxAttempts = 50,
  attempt = 0,
): void {
  if (!iframe || attempt >= maxAttempts) {
    if (attempt >= maxAttempts) {
      console.warn('Max attempts reached waiting for email elements');
    }
    return;
  }

  try {
    const iframeDoc = getIframeDocument(iframe);
    if (!iframeDoc) {
      setTimeout(
        () => waitForIframeAndPositionIcon(iframe, highlightedLinkId, maxAttempts, attempt + 1),
        50,
      );
      return;
    }

    if (iframeDoc.readyState === 'loading') {
      iframeDoc.addEventListener('DOMContentLoaded', () => {
        waitForIframeAndPositionIcon(iframe, highlightedLinkId, maxAttempts, attempt);
      });
      return;
    }

    const positioned = positionClickedLinkIcon(iframeDoc, highlightedLinkId);
    if (!positioned) {
      setTimeout(
        () => waitForIframeAndPositionIcon(iframe, highlightedLinkId, maxAttempts, attempt + 1),
        50,
      );
    }
  } catch (error) {
    console.error('Failed to calculate icon position:', error);
  }
}
